import * as React from "react"
import { gql } from "@apollo/client"
import { Box, Flex, Stack, Text } from "@chakra-ui/react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import { AgendaItemFragment } from "lib/graphql"

import { AgendaItem } from "./AgendaItem"
import { LinkButton } from "./LinkButton"

dayjs.extend(utc)

const _ = gql`
  fragment AgendaItem on AgendaItem {
    id
    name
    date
    type
    slug
    image
  }
`

interface Props {
  agenda: AgendaItemFragment[]
}

export function Agenda({ agenda }: Props) {
  const groupedItems = React.useMemo(
    () =>
      agenda?.reduce((acc, item) => {
        if (acc[item.date]) {
          acc[item.date].push(item)
        } else {
          acc[item.date] = [item]
        }
        return acc
      }, {} as { [date: string]: AgendaItemFragment[] }),
    [agenda],
  )

  return (
    <Stack
      spacing={2}
      py={6}
      px={4}
      top={28}
      maxH="80vh"
      w={{ base: "100%", md: "100%" }}
      position={{ base: "static", md: "sticky" }}
      bgColor="gray.700"
      borderRadius="xl"
      right={{ base: 6, md: 20, lg: 28 }}
      overflowY="auto"
      zIndex={50}
    >
      <Text color="white" fontSize="3xl" fontWeight="black" m={0}>
        Dit komt eraan voor jou!
      </Text>
      {agenda.length > 0 && groupedItems ? (
        Object.entries(groupedItems).map(([date, items], i) => {
          return (
            <Box key={date}>
              <Flex m={0} w="100%" justify="space-between" align="flex-end" mb={1} mt={4}>
                <Text
                  color="gray.400"
                  fontWeight="black"
                  textTransform="capitalize"
                  fontSize={{ base: "xl", md: "xl" }}
                >
                  {dayjs.utc(date).format("MMMM")}
                </Text>
              </Flex>
              <Stack spacing={4}>
                <Text mb={0} textTransform="capitalize" fontSize="sm" color="gray.400">
                  {dayjs.utc(date).format("dddd D")}
                </Text>
                {items.map((item) => (
                  <AgendaItem item={item} key={i} />
                ))}
              </Stack>
            </Box>
          )
        })
      ) : (
        <Stack spacing={4}>
          <Text color="white" fontSize={{ base: "sm", md: "md" }}>
            Je agenda is nog leeg. Tijd om 'm te vullen! 😊
          </Text>
          <LinkButton variant="outline" href="/te-doen" size="md" colorScheme="yellow">
            Leuke dingen om agenda
          </LinkButton>
        </Stack>
      )}
    </Stack>
  )
}
